import { getStoreSetting, updateStoreSetting } from '@/api/setting-api';
import { toast } from '@/hooks/use-toast';
import { StoreSetting } from '@/types/setting-type';
import { useCallback, useEffect, useState } from 'react';

const EMPTY_SETTINGS: StoreSetting = {
  id: '',
  name: '',
  phone: '',
  website: '',
  email: '',
  logo: '',
  addressLine1: '',
  addressLine2: '',
  autoDetectProductCode: false,
  showOutOfStock: false,
  autoConfirmOrder: false,
  isEnabledSendKhqr: false,
};

export function useStoreSetting() {
  const [setting, setSetting] = useState<StoreSetting | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetch() {
      try {
        const resp = await getStoreSetting();
        if (resp.success) {
          setSetting(resp.data);
        }
      } catch (error) {
        console.error('Failed to fetch store setting', error);
      } finally {
        setIsLoading(false);
      }
    }
    fetch();
  }, []);

  return { setting, isLoading };
}

export function useStoreSettingForm() {
  const [settings, setSettings] = useState<StoreSetting>(EMPTY_SETTINGS);
  const [isSaving, setIsSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const resp = await getStoreSetting();
      if (resp.success) setSettings(resp.data);
    } catch (error) {
      console.error('Failed to fetch store setting', error);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateField = useCallback(<K extends keyof StoreSetting>(field: K, value: StoreSetting[K]) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  }, []);

  const saveSettings = useCallback(async () => {
    try {
      setIsSaving(true);
      const resp = await updateStoreSetting(settings);
      if (resp.success) {
        toast({ title: 'Success', description: 'Store settings updated.' });
      }
    } finally {
      setIsSaving(false);
    }
  }, [settings]);

  return { settings, isSaving, updateField, saveSettings };
}
